import type { NestedTabsProps } from "./nested-tabs.type";

import { Tabs, Typography } from "antd";
import classNames from "classnames";

import { TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { useTabNavigation } from "@/hooks/use-tab-navigation";
import { useThemeBreakpoints } from "@/services/theme";

import "./nested-tabs.style.less";

const { Title } = Typography;

export const NestedTabs = ({
  items,
  tabKey = TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
  defaultActiveKey,
  className,
  paramsToKeep = [],
  tabBarExtraContent,
  alwaysSyncSearchParams = false,
  shouldSync,
  ...tabsProps
}: NestedTabsProps) => {
  const { isSmallScreen, isLargeScreen } = useThemeBreakpoints();

  const { visibleTabs, activeTabKey, onTabChange, location } = useTabNavigation(
    {
      items,
      tabKey,
      defaultActiveKey,
      paramsToKeep,
      alwaysSyncSearchParams,
      shouldSync,
    }
  );

  const hasSingleTab = visibleTabs.length === 1;
  const singleTab = hasSingleTab ? visibleTabs[0] : null;

  if (hasSingleTab && singleTab) {
    return (
      <div
        className={classNames("nested-tabs-single", {
          "nested-tabs-xl-large": isLargeScreen,
          "nested-tabs-xl-small": isSmallScreen,
          [className || ""]: !!className,
        })}
      >
        <Title level={3} className="single-tab-title">
          <span className="tab-icon">{singleTab.icon}</span>
          <span className="tab-label">{singleTab.label}</span>
        </Title>
        {singleTab.children}
      </div>
    );
  }

  return (
    <div
      className={classNames("nested-tabs", {
        "nested-tabs-xl-large": isLargeScreen,
        "nested-tabs-xl-small": isSmallScreen,
      })}
    >
      <Tabs
        {...tabsProps}
        className={className}
        items={visibleTabs}
        defaultActiveKey={defaultActiveKey}
        key={location.pathname}
        activeKey={activeTabKey}
        onChange={onTabChange}
        tabBarExtraContent={tabBarExtraContent}
        tabPlacement="top"
        type="card"
        size={isSmallScreen ? "small" : "medium"}
      />
    </div>
  );
};
